/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, Inject, OnInit, ViewChild } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatFormField } from "@angular/material/form-field";
import { MatStepper } from "@angular/material/stepper";
import { get } from "lodash";
import * as moment from "moment";
import { of } from "rxjs";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { QueryParams } from "../../../../core/http/query-params.type";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableConfig } from "../../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../../shared/components/data-table/data-table.type";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import {
  SimpleDialogComponent,
  SimpleDialogProperties,
} from "../../../../shared/components/simple-dialog/simple-dialog.component";
import { pattern } from "../../../../shared/constants/patterns";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { isBetweenValidator } from "../../../../shared/validators/is-between.validator";
import { AssetTableService } from "../../assets/asset-table.service";
import { AssetService } from "../../assets/asset.service";
import { AssetServiceUI } from "../../assets/asset.service.ui";
import { Asset, AssetRenewal } from "../../assets/asset.type";
import { validateDates } from "../../assets/assets.shared";
import { CreateAssetService } from "../../assets/create-asset/create-asset.service";
import { CompanyService } from "../../companies/company.service";
import { ConfigurableOption } from "../../configurable-options/configurable-options.type";
import { InternalProvider } from "../../internal-provider/internal-provider.type";
import { ContractService } from "../contract.service";
import { Contract, ContractRenewal, ContractStatus } from "../contract.type";
import { RenewContractService } from "./renew-contract.service";

export interface RenewContractComponentProps {
  contract: Contract;
  table: DataTableComponent<Contract>;
}

interface AssetData {
  values: Asset;
  updatedValues?: Partial<AssetRenewal>;
}

type DiscountType = "$" | "%";

enum RenewalSteps {
  contractData,
  existingAssets,
  newAssets,
  reviewAssets,
  confirmation,
}

@Component({
  selector: "app-renew-contract",
  templateUrl: "./renew-contract.component.html",
  styleUrls: ["./renew-contract.component.scss"],
  providers: [RenewContractService, CreateAssetService],
})
export class RenewContractComponent implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper;
  // Tables
  // @ViewChildren(DataTableComponent) tables: QueryList<DataTableComponent<unknown>>;
  @ViewChild("existingAssetsTable") existingAssetsTable: DataTableComponent<Asset>;
  @ViewChild("companyAssetsTable") companyAssetsTable: DataTableComponent<Asset>;
  @ViewChild("reviewAssetsTable") reviewAssetsTable: DataTableComponent<Asset>;
  // Dynamic forms
  @ViewChild("currentContractForm") currentContractForm: DynamicFormComponent;
  @ViewChild("newContractForm") newContractForm: DynamicFormComponent;
  @ViewChild("summaryContractForm") summaryContractForm: DynamicFormComponent;
  // Discount field
  @ViewChild("discountField") discountField: MatFormField;
  @ViewChild("discountInput") discountInput: ElementRef;

  // Initial required data
  currentUser: UserInfo;
  contract: RenewContractComponentProps["contract"];
  contractTable: RenewContractComponentProps["table"];
  contractStartDate: moment.Moment;
  contractEndDate: moment.Moment;
  isEditingRenewal: boolean;
  shouldUpdateTables: boolean;
  flags: {
    previousAssetsToRenew: boolean;
    selectedAssetsToRenew: boolean;
    selectedAssetsToAdd: boolean;
  } = { previousAssetsToRenew: false, selectedAssetsToRenew: false, selectedAssetsToAdd: false };
  loading: boolean;
  previousAssets: Asset[];
  totalPreviousPartnerPrice: number;

  /** Tables configurations */
  tablesConfigs: {
    existingAssets: DataTable<Asset>;
    companyAssets: DataTable<Asset>;
    reviewAssets: DataTable<Asset>;
  };

  /** Questions used in each form */
  questions: {
    current?: QuestionBase<string | boolean>[];
    new?: QuestionBase<string | boolean>[];
    summary?: QuestionBase<string | boolean | number>[];
  } = {};

  assetData: Map<string, AssetData> = new Map();

  /** Used to apply a discount */
  discountType: DiscountType = "$";
  regexPercent = pattern.percentage;
  regexPrice = pattern.price;

  discount = new FormControl("", Validators.pattern(this.discountType === "$" ? this.regexPrice : this.regexPercent));

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RenewContractComponentProps,
    public dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private assetTableService: AssetTableService,
    private renewContractService: RenewContractService,
    private companyService: CompanyService,
    private contractService: ContractService,
    private createAssetService: CreateAssetService,
    private assetService: AssetService,
    private assetServiceUI: AssetServiceUI,
    private dataTableService: DataTableService<Asset>,
    private notificationService: NotificationService,
    private loader: LoadingService,
  ) {}

  //#region Getters

  /** Get the count of the renewed assets */
  private get renewedAssetsCount(): number {
    return this.existingAssetsTable?.table.selected?.length || 0;
  }

  /** Get the count of the new assets */
  private get newAssetsCount(): number {
    return this.companyAssetsTable?.table.selected?.length || 0;
  }

  /** Get the count of the non renewed assets */
  private get deletedAssetsCount(): number {
    const previousAssetsCount = this.previousAssets.length;
    return previousAssetsCount - this.renewedAssetsCount;
  }

  /** Get the count of the renewed and new assets */
  private get assetsCount(): number {
    return this.renewedAssetsCount + this.newAssetsCount;
  }

  //#endregion

  //#region Lifecycle Methods

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.contract = this.data.contract;
    this.contractTable = this.data.table;
    // Set total previous partner price
    this.contractService.getTotalPrices(this.contract.id).subscribe((prices) => {
      this.totalPreviousPartnerPrice = prices.totalPartnerPrice;
    });

    // Create steps (forms and tables)
    this.createStepOne();
    this.createTables();
  }

  //#endregion

  //#region Populate & Update Table Methods

  getAllowedAssetsToRenew = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult } = event;
    const queryParams: QueryParams<Asset> = {
      ...event.queryParams,
      relations: {
        location: true,
        internalProvider: true,
        sla: true,
        contract: { maintenanceProvider: true },
        company: { partner: true },
      },
      where: {
        contractId: this.contract.id,
        ...event?.queryParams?.where,
      },
    };
    this.assetService.getAssets(queryParams).subscribe((data): void => {
      onResult(data);
      // Update table rows (each asset) and populate assetData for the first time (if it isn't)
      this.updateAssetsOnLoad(this.existingAssetsTable, { startDate: this.contractStartDate });
      // Select the assets only once
      if (!this.flags.selectedAssetsToRenew) {
        this.flags.selectedAssetsToRenew = true;
      }
      // Get assets of the previous contract version
      if (!this.flags.previousAssetsToRenew) {
        this.flags.previousAssetsToRenew = true;
        this.getPreviousAssets();
      }
    }, onError);
  };

  getAllowedAssetsToAdd = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult } = event;
    const queryParams: QueryParams<Asset> = {
      ...event.queryParams,
      relations: { internalProvider: true, sla: true },
      where: [
        {
          companyId: this.contract.companyId,
          contractId: { $ne: this.contract.id },
          coverageStatus: { $in: ["expired", "not covered", "terminated"] },
          ...event?.queryParams?.where,
        },
        // For some strange reason "contractId: { $ne: this.contract.id }" doesn't return null values
        // so we have to "duplicate" the filter changing the contractId condition to null
        {
          companyId: this.contract.companyId,
          contractId: null,
          coverageStatus: { $in: ["expired", "not covered", "terminated"] },
          ...event?.queryParams?.where,
        },
        {
          companyId: this.contract.companyId,
          contractId: { $ne: this.contract.id },
          coverageStatus: "active",
          endDate: { $lt: this.contractEndDate.toISOString() },
          ...event?.queryParams?.where,
        },
        // For some strange reason "contractId: { $ne: this.contract.id }" doesn't return null values
        // so we have to "duplicate" the filter changing the contractId condition to null (same case thant )
        {
          companyId: this.contract.companyId,
          contractId: null,
          coverageStatus: "active",
          endDate: { $lt: this.contractEndDate.toISOString() },
          ...event?.queryParams?.where,
        },
        ...(this.contract.status === ContractStatus.active
          ? [
              {
                companyId: this.contract.companyId,
                contractId: null,
                coverageStatus: "expired",
                ...event?.queryParams?.where,
              },
            ]
          : []),
      ],
    };
    this.assetService.getAssets(queryParams).subscribe((data): void => {
      onResult(data);
      // Unselect invalid assets
      this.unselectAssetsWithInvalidDates();
      // Update table rows (each asset) and populate assetData for the first time (if it isn't)
      this.updateAssetsOnLoad(this.companyAssetsTable);

      // Select the assets only once
      if (!this.flags.selectedAssetsToAdd) {
        this.flags.selectedAssetsToAdd = true;
        this.unselectAssetsWithInvalidDates();
      }

      // Disable assets/rows pending in other contracts
      this.disableRows(data.items);
    }, onError);
  };

  getAssetsToReview = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult } = event;
    // Renewed assets
    const renewedAssets = ((this.existingAssetsTable?.table.selected || []) as Asset[]).map((asset) =>
      this.getMergedAsset(asset.id),
    );

    // New assets
    const newAssets: Asset[] = ((this.companyAssetsTable?.table.selected || []) as Asset[]).map((asset) =>
      this.getMergedAsset(asset.id),
    );

    // Deleted assets
    const renewedAssetIds = renewedAssets.map((asset: Asset) => asset.id);
    const deletedAssets = (this.previousAssets
      ?.filter((asset: Asset) => !renewedAssetIds.includes(asset.id))
      .map((asset) => this.getMergedAsset(asset.id)) || []) as Asset[];

    const assetsToReview = [...renewedAssets, ...newAssets, ...deletedAssets] as Asset[];
    of(assetsToReview).subscribe(onResult, onError);

    if (this.reviewAssetsTable) {
      this.reviewAssetsTable.config.disableRows = [...deletedAssets.map((asset) => asset.id)];
    }
  };

  updateAsset = (event: UpdateDataEvent<Asset, AssetRenewal>, table: DataTableComponent<Asset>): void => {
    const { onError, onResult, row } = event;
    let { updatedValues } = event;

    const id = row.id;

    if (id && updatedValues) {
      if (updatedValues.slaId !== undefined) {
        const { slaId } = updatedValues;
        const sla = table.searchOptions.find((opt) => opt.id === slaId) || null;
        updatedValues = { ...updatedValues, sla };
      }

      this.updateAssetRow(row, updatedValues);
      onResult(updatedValues);

      // After the value is updated, we update the selected rows
      if (table) {
        table.config.selection.selectedRows = [
          ...table.config.selection.selectedRows.filter((asset) => asset.id !== row.id),
          row,
        ];
      }
    } else {
      onError();
    }
  };

  //#endregion

  //#region Handlers Methods

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onStepChange = async ({ previouslySelectedIndex: previouslyStepIndex, selectedIndex: stepIndex }): Promise<void> => {
    if (RenewalSteps.contractData === previouslyStepIndex && this.shouldUpdateTables) {
      this.refreshTables();
      this.shouldUpdateTables = false;
    }

    // Step 4
    if (RenewalSteps.reviewAssets === stepIndex) {
      this.reviewAssetsTable.search();
    }

    // Step 5
    if (RenewalSteps.confirmation === stepIndex) {
      this.buildSummaryRenewalForm();
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onNewContractFormChange = (event: any): void => {
    if (event.key === "startDate") {
      validateDates(event, this.newContractForm);
      const startDate = moment(event.value).startOf("day").toDate().toISOString();
      this.setContractStartDate(startDate);
      this.shouldUpdateTables = true;

      /** @workaround to refresh table */
      this.existingAssetsTable.scrollX();
      this.companyAssetsTable.scrollX();
    }
    if (event.key === "endDate") {
      const endDate = moment(event.value).startOf("day").toDate().toISOString();
      this.setContractEndDate(endDate);
      this.shouldUpdateTables = true;
    }
  };

  onSubmitRenewal = (): void => {
    if (this.assetsCount === 0) {
      this.notificationService.error("At least one asset should be selected");
      return;
    }
    const formValues = this.newContractForm.form.value;
    const renewedAssets = this.getRenewedAssets();
    const newAssets = this.getNewAssets();

    const getAssetToSubmit = (assetData: AssetRenewal): AssetRenewal => {
      const asset: AssetRenewal = {
        id: assetData.id,
        name: assetData.name,
        description: assetData.description,
        category: assetData.category,
        dmr: assetData.dmr,
        monitoring: assetData.monitoring,
        slaId: assetData.slaId,
        partnerPrice: assetData.partnerPrice,
        channelWorksCost: assetData.channelWorksCost,
        internalProviderId: assetData.internalProviderId,
      };
      return asset;
    };

    const data: ContractRenewal = {
      contractId: this.contract.id,
      name: formValues.name,
      startDate: moment(formValues.startDate).utc().startOf("day").toDate(),
      endDate: moment(formValues.endDate).utc().startOf("day").toDate(),
      ...(formValues.notes && { notes: formValues.notes }),
      ...(formValues.configurationNotes && { configurationNotes: formValues.configurationNotes }),
      ...(formValues.sfOpportunity && { sfOpportunity: formValues.sfOpportunity }),
      renewedAssets: Object.keys(renewedAssets).map((id) => {
        const { values, updatedValues } = renewedAssets[id];
        const assetData = { ...values, ...updatedValues } as AssetRenewal;
        const asset = getAssetToSubmit(assetData);
        return asset;
      }),
      newAssets: Object.keys(newAssets).map((id) => {
        const { values, updatedValues } = newAssets[id];
        const assetData = { ...values, ...updatedValues } as AssetRenewal;
        const asset = getAssetToSubmit(assetData);
        asset.startDate = assetData.startDate;
        return asset;
      }),
      maintenanceProviderId: formValues.maintenanceProviderId,
    };

    this.loading = true;
    this.renewContract(data);
  };

  onResetRenewal = (stepper: MatStepper): void => {
    this.resetRenewal(stepper);
  };

  onCloseRenewal = (): void => {
    const dialog = this.dialog.open<SimpleDialogComponent, SimpleDialogProperties>(SimpleDialogComponent, {
      id: "contractRenewalModal",
      panelClass: "custom-dialog-container",
      data: {
        title: "Close renewal window",
        message: "Are you sure you want to close this window? You will lose all your progress.",
        type: "yesNo",
        action: "true",
      },
    });

    dialog.afterClosed().subscribe((result) => {
      const close = result === "true";
      if (close) {
        this.dialog.closeAll();
      }
    });
  };

  /** Update discount type. Only for step 4  */
  onDiscountTypeChange = (): void => {
    if (this.discountType === "$") {
      this.discountType = "%";
      this.discount.setValidators([Validators.pattern(this.regexPercent)]);
    } else {
      this.discountType = "$";
      this.discount.setValidators([Validators.pattern(this.regexPrice)]);
    }
    this.discountInput.nativeElement.value = "";
  };

  /** Apply discount. Only for step 4  */
  onApplyDiscount = (value: string): void => {
    const discount = +value;

    const applyDiscount = (
      assets: AnyObject<{
        values: Asset;
        updatedValues?: Partial<AssetRenewal>;
      }>,
    ) => {
      Object.keys(assets)
        .filter((assetId) =>
          this.reviewAssetsTable.config.selection.selectedRows.map((asset) => asset.id).some((id) => id === assetId),
        )
        .forEach((id) => {
          let partnerPrice =
            this.assetData?.get(id)?.updatedValues?.partnerPrice ?? (+assets[id].values?.partnerPrice || 0);

          if (this.discountType === "$") {
            partnerPrice -= discount;
          } else {
            partnerPrice -= (partnerPrice * discount) / 100;
          }

          // Avoid fractional and negative numbers
          if (partnerPrice < 0.01) {
            partnerPrice = 0;
          }

          this.assetData.set(id, {
            values: this.assetData?.get(id)?.values || assets[id].values,
            updatedValues: { ...this.assetData?.get(id)?.updatedValues, partnerPrice },
          });

          (this.reviewAssetsTable.table._rows as Asset[]).find((asset) => {
            if (asset.id === id) {
              asset.partnerPrice = this.getFormattedPrice(partnerPrice);
            }
          });
        });
    };

    const renewedAssets = this.getRenewedAssets();
    const newAssets = this.getNewAssets();

    applyDiscount(renewedAssets);
    applyDiscount(newAssets);

    /** @workaround to refresh table */
    this.reviewAssetsTable.scrollX();
  };

  //#endregion

  //#region Component Methods

  /** Get the message to be shown below in each steap */
  getAssetsListMessage = (stepper: MatStepper): string => {
    let message: string;
    const stepIndex = stepper.selectedIndex;
    if (RenewalSteps.reviewAssets === stepIndex) {
      message = `Total assets: ${this.assetsCount}`;
    }
    return message;
  };

  hasPreviousStep = (stepper: MatStepper): boolean => stepper.selectedIndex > 0;

  hasNextStep = (stepper: MatStepper): boolean => stepper.selectedIndex < stepper.steps.length - 1;

  isLastStep = (stepper: MatStepper): boolean => stepper.selectedIndex === stepper.steps.length - 1;

  goPreviousStep = (stepper: MatStepper): void => stepper.previous();

  goNextStep = (stepper: MatStepper): void => {
    if (stepper.selectedIndex === 0) {
      if (this.newContractForm.form.valid) {
        stepper.next();
      } else {
        Object.keys(this.newContractForm.form.controls).forEach((key) => {
          this.newContractForm.form.controls[key].markAsTouched();
        });
      }
    } else {
      stepper.next();
    }
  };

  //#endregion

  //#region Other Methods

  /** Create step 1 */
  private createStepOne = (): void => {
    // Create forms for the step
    this.createCurrentContractForm();
    this.createNewRenewalForm();

    // Set initials dates
    this.setContractStartDate(this.renewContractService.getNewStartDate(this.contract.endDate));
  };

  /** It unselects company assets with dates same or after the contract end date */
  private unselectAssetsWithInvalidDates = () => {
    this.companyAssetsTable.config.selection.selectedRows =
      this.companyAssetsTable.config.selection.selectedRows.filter((asset) => {
        const shouldRemoveAsset = moment(asset.endDate).isSameOrAfter(this.contractEndDate);
        if (shouldRemoveAsset) {
          this.assetData.delete(asset.id);
        }
        return !shouldRemoveAsset;
      });
  };

  /** Return an asset from assetData map mergin its props (values and updatedValues) */
  private getMergedAsset = (assetId: string): Asset => {
    const asset = this.assetData.get(assetId);
    const { startDate, endDate, ...restUpdatedValues } = asset.updatedValues;
    return {
      ...asset.values,
      ...restUpdatedValues,
      startDate: startDate?.toISOString() || asset.values?.startDate,
      endDate: endDate?.toISOString() || asset.values?.endDate,
    };
  };

  /** Get the selected assets to renew */
  private getRenewedAssets(): AnyObject<AssetData> {
    const selectedAssets = [...(this.existingAssetsTable?.table.selected || [])] as Asset[];
    const renewedAssets: AnyObject<AssetData> = {};

    selectedAssets.forEach(
      (selectedAsset) =>
        (renewedAssets[selectedAsset.id] = {
          ...(this.assetData.get(selectedAsset.id) || {}),
          values: this.assetData.get(selectedAsset.id)?.values || selectedAsset,
        }),
    );

    return renewedAssets;
  }

  /** Get the selected assets to renew */
  private getNewAssets(): AnyObject<AssetData> {
    const selectedAssets = [...(this.companyAssetsTable?.table.selected || [])] as Asset[];
    const newAssets: AnyObject<AssetData> = {};

    selectedAssets.forEach(
      (selectedAsset) =>
        (newAssets[selectedAsset.id] = {
          ...(this.assetData.get(selectedAsset.id) || {}),
          values: this.assetData.get(selectedAsset.id)?.values || selectedAsset,
        }),
    );

    return newAssets;
  }

  /** Get the selected assets to renew */
  private getAssetsForTotalPrice = (includeUpdatedValues = true): (Asset | AssetRenewal)[] => {
    const renewedAssets = this.existingAssetsTable?.table.selected.map((asset) => this.getMergedAsset(asset.id)) || [];
    const newAssets = this.companyAssetsTable?.table.selected.map((asset) => this.getMergedAsset(asset.id)) || [];

    const assetsToReview = [...renewedAssets, ...newAssets] as Asset[];
    const assetIdsToReview = assetsToReview.map((asset) => asset.id);

    if (this.isEditingRenewal) {
      return assetIdsToReview.map((id) => ({
        ...this.assetData?.get(id)?.values,
        ...(includeUpdatedValues && this.assetData?.get(id)?.updatedValues),
      })) as AssetRenewal[];
    } else {
      return assetsToReview;
    }
  };

  private getDeletedAssets(): AnyObject<AssetData> {
    const renewedAssetIds = ([...(this.existingAssetsTable?.table.selected || [])] as Asset[]).map(
      (asset: Asset) => asset.id,
    );
    const filteredAssets = (this.previousAssets?.filter((asset: Asset) => !renewedAssetIds.includes(asset.id)) ||
      []) as Asset[];

    const deletedAssets: AnyObject<AssetData> = {};

    filteredAssets.forEach(
      (asset) =>
        (deletedAssets[asset.id] = {
          values: this.assetData.get(asset.id)?.values ? { ...this.assetData.get(asset.id)?.values } : { ...asset },
          ...(this.assetData.get(asset.id) || {}),
        }),
    );

    return deletedAssets;
  }

  private setContractStartDate(startDate: string): void {
    this.contractStartDate = startDate ? moment(startDate).utc() : undefined;
  }

  private setContractEndDate(endDate: string): void {
    this.contractEndDate = endDate ? moment(endDate).utc() : undefined;
  }

  /**
   * Update table rows (also assetData map)
   * @param table table to update
   * @param updatedValues a optional param to force some asset values to be updated
   */
  private updateAssetsOnLoad = (table: DataTableComponent<Asset>, updatedValues?: Partial<AssetRenewal>): void => {
    table.config.rows.forEach((asset) => this.updateAssetRow(asset, updatedValues));
  };

  /** Update asset and assetData map */
  private updateAssetRow = (asset: Asset, updatedValues?: Partial<AssetRenewal>): void => {
    const id = asset.id;

    const _values = this.assetData.get(id)?.values || {
      ...asset,
    };

    // Important: The first time the table is loaded, this constant will probably be an empty object,
    // so for the term properties we must make sure that their values are correctly assigned
    const _updatedValues = { ...this.assetData.get(id)?.updatedValues, ...updatedValues };

    //#region Set Terms
    // Set "start date" property (term)
    let startDate = _updatedValues?.startDate || this.contractStartDate;

    if (startDate?.isBefore(this.contractStartDate)) {
      startDate = this.contractStartDate;
    } else if (startDate?.isAfter(this.contractEndDate)) {
      startDate = this.contractEndDate;
    }

    // Set "sla" property (term)
    let sla: ConfigurableOption;
    let slaId: string;

    if (_updatedValues?.sla !== undefined) {
      sla = _updatedValues.sla;
      slaId = _updatedValues.slaId;
    } else {
      sla = asset.sla;
      slaId = asset.slaId;
    }

    // Set "Internal Provider" (term)
    let internalProvider: InternalProvider;
    let internalProviderId: string;

    if (_updatedValues?.internalProvider !== undefined) {
      internalProvider = _updatedValues.internalProvider;
      internalProviderId = _updatedValues.internalProviderId;
    } else {
      internalProvider = asset.internalProvider;
      internalProviderId = asset.internalProviderId;
    }

    // Set "dmr" and "monitoring" properties (terms)
    const dmr = _updatedValues?.dmr ?? asset.dmr;
    const monitoring = _updatedValues?.monitoring ?? asset.monitoring;

    // Set "partnerPrice" and "channelWorksCost" properties (terms)
    const getPrice = (price: number) => (price !== null && !isNaN(price) ? price : null);

    const partnerPrice = getPrice(Number(_updatedValues?.partnerPrice)) ?? Number(asset.partnerPrice);

    const channelWorksCost = getPrice(Number(_updatedValues?.channelWorksCost)) ?? Number(asset.channelWorksCost);

    const previousPartnerCost =
      getPrice(Number(asset.previousPartnerPrice)) ?? (getPrice(Number(asset.partnerPrice)) || 0);

    const previousChannelWorksCost =
      getPrice(Number(asset.previousChannelWorksCost)) ?? (getPrice(Number(asset.channelWorksCost)) || 0);

    //#endregion

    // Set rest of properties (no terms)
    const name = _updatedValues?.name ?? asset.name;
    const description = _updatedValues?.description ?? asset.description;
    const category = _updatedValues?.category ?? asset.category;

    // Assign/Update asset (table row)
    asset.name = name;
    asset.description = description;
    asset.category = category;
    asset.startDate = startDate.toDate().toISOString();
    asset.sla = sla;
    asset.slaId = slaId;
    asset.dmr = dmr;
    asset.monitoring = monitoring;
    asset.previousPartnerPrice = previousPartnerCost;
    asset.previousChannelWorksCost = previousChannelWorksCost;
    asset.partnerPrice = partnerPrice;
    asset.channelWorksCost = channelWorksCost;
    asset.internalProvider = internalProvider;
    asset.internalProviderId = internalProviderId;

    // Assign/Update asset (request data)
    this.updateAssetDataMap(
      {
        ..._values,
        ...(startDate.isSame(this.contractStartDate) && { startDate: asset.startDate }),
        ...(this.isEditingRenewal && { startDate: this.contractStartDate.toDate().toISOString() }),
        previousChannelWorksCost: previousChannelWorksCost,
        previousPartnerPrice: previousPartnerCost,
      },
      {
        ..._updatedValues,
        ...(!startDate.isSame(this.contractStartDate) && { startDate }),
        ...(this.isEditingRenewal && {
          sla,
          slaId,
          dmr,
          monitoring,
          partnerPrice,
          channelWorksCost: channelWorksCost,
          internalProvider,
          internalProviderId,
        }),
      },
    );

    // Check and clean start date in updatedValues if it's needed
    if (this.assetData.get(id)?.updatedValues?.startDate?.isSameOrBefore(this.contractStartDate)) {
      delete this.assetData.get(id).updatedValues.startDate;
    }
  };

  private updateAssetDataMap = (values: Asset, updatedValues: Partial<AssetRenewal>): void => {
    const id = values.id;
    this.assetData.set(id, {
      values,
      updatedValues,
    });
  };

  /** This method is executed only once when the table loads the data for the first time.
   *
   * It's used to get the previous assets (rows)
   */
  private getPreviousAssets = (): void => {
    const queryParams: QueryParams<Asset> = {
      relations: {
        location: true,
        internalProvider: true,
        sla: true,
        contract: { maintenanceProvider: true },
        company: { partner: true },
      },
      where: {
        contractId: this.contract.id,
      },
      pagination: { page: 1, pageSize: 500 },
    };

    this.assetService.getAssets(queryParams).subscribe((data) => {
      this.previousAssets = data.items;
      this.previousAssets.forEach((asset) => this.updateAssetRow(asset));
    });
  };

  /** Create table configs */
  private createTables = (): void => {
    const defaultTableConfig: DataTableConfig<Asset> = {
      initialSearch: this.isEditingRenewal,
      bodyHeight: 280,
      syncWithQueryParams: false,
      selection: { hidden: false },
      shown: [
        "name",
        "serialNumber",
        "description",
        "category",
        "sla.value",
        "type.model.oem.name",
        "type.model.name",
        "type.name",
        "startDate",
        "endDate",
        "dmr",
        "monitoring",
        "internalProvider.name",
      ],
      actionColumn: { hidden: true },
      showNotificationAfterUpdate: false,
      columns: {
        serialNumber: {
          link: {
            path: null,
            disabled: true,
          },
          inlineEdit: { disabled: () => true },
        },
        dmr: {
          hidden: false,
        },
        monitoring: {
          hidden: false,
        },
        endDate: {
          inlineEdit: { disabled: () => true },
        },
        "internalProvider.name": {
          link: {
            path: null,
            disabled: true,
          },
        },
      },
      toolTip: this.getToolTip,
    };

    this.tablesConfigs = {
      existingAssets: this.assetTableService.getAssetsTableConfig({
        ...defaultTableConfig,
        id: "existingAssets",
        initialSearch: false,
        shown: [...defaultTableConfig.shown, "coverageStatus"],
        columns: {
          ...defaultTableConfig.columns,
          coverageStatus: {
            inlineEdit: { disabled: () => true },
          },
          startDate: {
            inlineEdit: { disabled: () => true },
          },
        },
      }),
      companyAssets: this.assetTableService.getAssetsTableConfig({
        ...defaultTableConfig,
        id: "companyAssets",
        initialSearch: false,
        shown: [...defaultTableConfig.shown, "info"],
        columns: {
          ...defaultTableConfig.columns,
          info: {
            hidden: false,
            icon: {
              name: (asset) => this.getInfoIcon(asset).name,
              color: (asset) => this.getInfoIcon(asset).color,
              tooltip: (asset) => this.getInfoIcon(asset).tooltip,
            },
          },
          startDate: {
            inlineEdit: {
              control: "date",
              validators: () => [isBetweenValidator(this.contractStartDate, this.contractEndDate, "[]")],
              date: {
                min: () => this.contractStartDate.toDate(),
                max: () => this.contractEndDate.toDate(),
              },
            },
          },
          endDate: {
            ...defaultTableConfig.columns.endDate,
            render: () => this.contractEndDate.toDate(),
          },
        },
      }),
      reviewAssets: this.assetTableService.getAssetsTableConfig({
        ...defaultTableConfig,
        id: "reviewAssets",
        clientSideHandling: true,
        shown: [
          "action",
          "name",
          "serialNumber",
          "sla.value",
          "type.model.oem.name",
          "type.model.name",
          "type.name",
          "previousChannelWorksCost",
          "channelWorksCost",
          "previousPartnerPrice",
          "partnerPrice",
        ],
        columns: {
          ...defaultTableConfig.columns,
          action: {
            hidden: false,
            icon: {
              name: (asset) => this.getActionIcon(asset.id).name,
              color: (asset) => this.getActionIcon(asset.id).color,
              tooltip: (asset) => this.getActionIcon(asset.id).tooltip,
            },
          },
          name: {
            inlineEdit: { disabled: () => true },
          },
          "sla.value": {
            inlineEdit: { disabled: () => true },
          },
          previousChannelWorksCost: {
            hidden: false,
            render: (asset) => this.getFormattedPrice(asset.previousChannelWorksCost ?? Number(asset.channelWorksCost)),
            toolTip: (asset) =>
              this.getFormattedPrice(asset.previousChannelWorksCost ?? Number(asset.channelWorksCost), true),
          },
          channelWorksCost: {
            hidden: false,
            render: (asset) => this.getCurrentPrice(asset, "channelWorksCost"),
            toolTip: (asset) => `$ ${this.getCurrentPrice(asset, "channelWorksCost")}`,
          },
          previousPartnerPrice: {
            name: "Previous Price",
            hidden: false,
            render: (asset) => this.getFormattedPrice(asset.previousPartnerPrice ?? Number(asset.partnerPrice)),
            toolTip: (asset) => this.getFormattedPrice(asset.previousPartnerPrice ?? Number(asset.partnerPrice), true),
            summaryFunc: () => this.getFormattedPrice(this.totalPreviousPartnerPrice, true),
          },
          partnerPrice: {
            name: "Price",
            hidden: false,
            render: (asset) => this.getCurrentPrice(asset, "partnerPrice"),
            toolTip: (asset) => `$ ${this.getCurrentPrice(asset, "partnerPrice")}`,
            summaryFunc: () => this.getTotalSumOf(this.getAssetsForTotalPrice(), "partnerPrice"),
          },
        },
      }),
    };

    // Props for step 2 & 3

    const enabledProps = ["sla.value", "dmr", "monitoring", "internalProvider.name"];

    this.tablesConfigs.existingAssets.columns.forEach((col) => {
      if (enabledProps.includes(col.prop)) {
        col.inlineEdit.disabled = () => false;
      }
    });

    this.tablesConfigs.companyAssets.columns.forEach((col) => {
      if (enabledProps.includes(col.prop)) {
        col.inlineEdit.disabled = () => false;
      }
    });

    // Props for step 4

    const enabledPricesProps = ["channelWorksCost", "partnerPrice"];

    this.tablesConfigs.reviewAssets.columns.forEach((col) => {
      if (enabledPricesProps.includes(col.prop)) {
        col.inlineEdit.disabled = () => false;
      }
    });

    this.tablesConfigs.existingAssets.defaultColumns = [];
    this.tablesConfigs.existingAssets.toolBar.exportCsv.hidden = true;

    this.tablesConfigs.companyAssets.defaultColumns = [];
    this.tablesConfigs.companyAssets.toolBar.exportCsv.hidden = true;
    this.tablesConfigs.companyAssets.toolBar.addItem = { function: this.addAsset };

    this.tablesConfigs.reviewAssets.defaultColumns = [];
    this.tablesConfigs.reviewAssets.toolBar.exportCsv.hidden = true;
    this.tablesConfigs.reviewAssets.summary = {
      height: 55,
      position: "bottom",
      hidden: false,
    };
  };

  /** Build the form for the current contract. Used on step 1 */
  private createCurrentContractForm(): void {
    this.renewContractService
      .generateQuestionsForCurrentRenewal({
        contract: this.contract,
        currentUser: this.currentUser,
        maintenanceProviderValue: this.contract.maintenanceProvider,
      })
      .subscribe((qs) => {
        this.questions.current = qs;
        this.loader.hide();
      });
  }

  /** Build the form for the new contract or the edited one. Used on step 1 */
  private createNewRenewalForm(): void {
    this.renewContractService
      .generateQuestionsForNewRenewal({
        contract: this.contract,
        currentUser: this.currentUser,
        maintenanceProviderValue: this.contract.maintenanceProvider,
      })
      .subscribe((qs) => {
        this.questions.new = qs;
        this.loader.hide();
      });
  }

  /** Build the form with the summary of the new contract renewal */
  private buildSummaryRenewalForm() {
    const question = this.newContractForm.questions.find(
      (qs) => qs.key === "maintenanceProviderId",
    ) as DropdownQuestion;

    const maintenanceProvidersList = question?.options;

    const formValues = this.newContractForm.form.value;
    const totalPreviousPrice = this.getFormattedPrice(this.totalPreviousPartnerPrice, true);
    const totalPrice = this.getTotalSumOf(this.getAssetsForTotalPrice(), "partnerPrice");

    this.renewContractService
      .generateQuestionsForSummaryRenewal({
        name: formValues.name,
        newNumberVersion: `${this.contract.number}-${this.contract.version + 1}`,
        renewedAssetsCount: `${this.renewedAssetsCount}`,
        newAssetsCount: `${this.newAssetsCount}`,
        deletedAssetsCount: `${this.deletedAssetsCount}`,
        startDate: `${moment(formValues.startDate).utc().toISOString()}`,
        endDate: `${moment(formValues.endDate).utc().toISOString()}`,
        sfOpportunity: `${formValues.sfOpportunity || ""}`,
        totalPreviousPrice,
        totalPrice,
        notes: `${formValues.notes || ""}`,
        configurationNotes: `${formValues.configurationNotes || ""}`,
        maintenanceProvider:
          maintenanceProvidersList.find((mp) => mp.value === formValues.maintenanceProviderId)?.label || "",
      })
      .subscribe((qs) => {
        this.questions.summary = qs;
        this.loader.hide();
      });
  }

  /** Add a new Asset. Only for step 3  */
  private addAsset = (): void => {
    this.companyService.getCompany(this.contract.companyId, { relations: { partner: true } }).subscribe((company) => {
      this.assetServiceUI.showAssetCreationModal(
        company,
        null,
        this.currentUser,
        this.companyAssetsTable,
        this.createAssetService,
        this.contractService,
        this.assetService,
      );
    });
  };

  private refreshTables = (): void => {
    this.existingAssetsTable.search();
    this.companyAssetsTable.search();
  };

  /** Reset renewal (all modified data) */
  private resetRenewal = (stepper: MatStepper): void => {
    stepper.reset();

    // Reset flags
    this.flags.selectedAssetsToRenew = false;
    this.flags.selectedAssetsToAdd = false;

    // Reset form
    const name = "";
    const contractNumber = `${this.contract.number}-${this.contract.version + 1}`;
    const startDate = this.renewContractService.getNewStartDate(this.contract.endDate);
    const endDate = "";
    const sfOpportunity = "";
    const notes = "";
    const configurationNotes = "";

    this.newContractForm.form.reset({
      name,
      contractNumber,
      startDate,
      endDate,
      sfOpportunity,
      notes,
      configurationNotes,
    });

    this.resetAssets();
    this.resetSteps();
  };

  /** Reset asset data */
  private resetSteps = (): void => {
    this.existingAssetsTable.reset(true);
    this.companyAssetsTable.reset(true);
    this.reviewAssetsTable.reset(true);
  };

  /** Reset asset data */
  private resetAssets = (): void => {
    this.assetData.clear();
  };

  /** Get generic tooltip for table cells */
  private getToolTip = (asset: Asset, column: DataTableColumn<Asset>): string => {
    let updatedValue = get(asset, column.prop); // The first param must be "asset" (the row) to display the correct value
    let previousValue = get(this.assetData.get(asset.id)?.values, column.prop);

    // Here we have to parse the data to get the correct value to be displayed
    updatedValue = this.dataTableService.parseData({ [column.prop]: updatedValue }, column, updatedValue, false);
    previousValue = this.dataTableService.parseData({ [column.prop]: previousValue }, column, previousValue, false);

    if (updatedValue === undefined || updatedValue === previousValue) {
      return null;
    }

    if (previousValue === undefined || previousValue === null || previousValue === "") {
      return updatedValue as string;
    }

    return `${updatedValue} (${previousValue})`;
  };

  /** Get info icon for assets. Only for step 3 */
  private getInfoIcon = (asset: Asset): { name: string; color: string; tooltip: string } => {
    // TODO: review this
    // if (this.isScheduledAssetInOtherContract(asset)) {
    //   return { name: "info", color: "#fd7e14", tooltip: "The asset is scheduled in another contract" };
    // }
    return { name: null, color: null, tooltip: null };
  };

  /** Get action icon for assets. Only for step 4 */
  private getActionIcon = (assetId: string): { name: string; color: string; tooltip: string } => {
    const renewedAssets = Object.values(this.getRenewedAssets()).map((asset) => ({
      ...asset.values,
      ...asset.updatedValues,
    })) as Asset[];

    const newAssets = Object.values(this.getNewAssets()).map((asset) => ({
      ...asset.values,
      ...asset.updatedValues,
    })) as Asset[];

    const deletedAssets = Object.values(this.getDeletedAssets()).map((asset) => ({
      ...asset.values,
    })) as Asset[];

    if (renewedAssets.some((asset) => asset.id === assetId)) {
      return { name: "autorenew", color: "blue", tooltip: "Asset to be renewed" };
    }
    if (newAssets.some((asset) => asset.id === assetId)) {
      return { name: "add", color: "green", tooltip: "Asset to be added" };
    }
    if (deletedAssets.some((asset) => asset.id === assetId)) {
      return { name: "clear", color: "red", tooltip: "Asset to be deleted" };
    }
  };

  /** Get current asset price depending if it's selected or not. Only for step 4 */
  private getCurrentPrice = (asset: Asset, key: keyof Asset): string => {
    const price = Object.values(this.getDeletedAssets())
      .map((_asset) => _asset.values.id)
      .some((id) => id === asset.id)
      ? "0"
      : `${asset[key]}`;

    return this.getFormattedPrice(Number(price));
  };

  /** Get the total sum of prices */
  private getTotalSumOf<T extends { id?: string }>(assets: T[], prop: keyof T): string {
    const total = assets.reduce((partialSum, asset) => {
      if (this.getDeletedAssets()[asset.id]) {
        return partialSum;
      }
      return partialSum + (asset[prop] ? Number(asset[prop]) : 0);
    }, 0);

    return this.getFormattedPrice(total, true);
  }

  private getFormattedPrice = (price: number, includeSymbol = false) => {
    if (!price) {
      price = 0;
    }
    return includeSymbol ? `$ ${price.toFixed(2)}` : price.toFixed(2);
  };

  /** Create a new contract renewal */
  private renewContract(data: ContractRenewal) {
    this.contractService
      .renewContract(data)
      .subscribe(
        (contract) => {
          this.contract = contract;
          this.dialog.closeAll();
          const message = "The contract has been renewed";
          this.notificationService.success(message);
          this.contractTable.search();
        },
        (error) => this.notificationService.error(error || "Could not renew contract"),
      )
      .add(() => {
        this.loading = false;
        this.loader.hide();
      });
  }

  private disableRows(assets: Asset[]): void {
    // TODO: review this
    //const assetIds = assets.filter(this.isScheduledAssetInOtherContract).map((asset) => asset.id);
    // this.companyAssetsTable.config.disableRows = [...assetIds];
  }

  //#endregion
}
